<template>
  <div class="prepeers-select">
    <label
      v-if="props.label"
      :for="selectId"
      class="prepeers-select__label">
      {{ props.label }}
    </label>
    <select
      :id="selectId"
      class="prepeers-select__field"
      :value="props.modelValue"
      :disabled="props.disabled"
      @change="onChange">
      <option
        v-for="opt in props.options"
        :key="opt.value === '' ? '__empty__' : opt.value"
        :value="opt.value"
        :selected="opt.value === props.modelValue">
        {{ opt.label }}
      </option>
    </select>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'
import type { ComputedRef, PropType } from 'vue'

import type { PrePeersSelectOption, PrePeersSelectProps } from '~/types/PrePeersSelect'

/**
 * Définit les props du composant PrePeersSelect.
 */
const props: PrePeersSelectProps = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  options: {
    type: Array as PropType<PrePeersSelectOption[]>,
    required: true,
  },
  id: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  label: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const autoId: string = useId()

const selectId: ComputedRef<string> = computed((): string => props.id ?? autoId)

/**
 * @param {Event} event - Événement `change` du `<select>` natif.
 * @returns {void}
 */
function onChange(event: Event): void {
  emit('update:modelValue', (event.target as HTMLSelectElement).value)
}
</script>

<style scoped>
.prepeers-select {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  flex: 1 1 200px;
}

.prepeers-select__label {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
}

.prepeers-select__field {
  width: 100%;
  padding: 0.7em 1.1em;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  cursor: pointer;
  outline: none;
  transition:
    border-color var(--duration-base) var(--ease-out),
    box-shadow var(--duration-base) var(--ease-out);
}

.prepeers-select__field:focus {
  border-color: var(--accent-blue);
  box-shadow: 0 0 0 3px rgba(3, 160, 241, 0.2);
}

.prepeers-select__field:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
